// REACT IMPORTS
import React, { useEffect, useState } from "react";

// MSAL
import { useMsal } from "@azure/msal-react";

// API
import { patchAPICall, postAPICall } from "../../../config/apiCalls";

// MUI COMPONENTS
import {
	Alert,
	Autocomplete,
	Button,
	Checkbox,
	CircularProgress,
	Collapse,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	InputAdornment,
	TextField,
} from "@mui/material";

// MUI ICONS
import CloseIcon from "@mui/icons-material/Close";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

// On library products, HubSpot's description holds the stage
const STAGES = ["Diagnose", "Prescribe", "Treat", "Claim"];

// Most products are $200/h
const DEFAULT_RATE = "200";

const formFrom = (product) => ({
	text: product?.text || "",
	description: product?.description || "",
	rate: !product
		? DEFAULT_RATE
		: product.rate === null || product.rate === undefined
			? ""
			: String(product.rate),
	phaseNames: product?.phaseNames || [],
	services: product?.services || [],
	components: product?.components || [],
});

// Multiple-choice field with checkboxes, like the table filters
const MultiSelect = ({
	label,
	options,
	value,
	onChange,
	helperText,
	getOptionLabel = (v) => v,
	sx,
}) => (
	<Autocomplete
		multiple
		disableCloseOnSelect
		options={options}
		value={value}
		onChange={(_, v) => onChange(v)}
		getOptionLabel={getOptionLabel}
		sx={sx}
		renderOption={(props, option, { selected }) => {
			const { key, ...optionProps } = props;
			return (
				<li key={key} {...optionProps}>
					<Checkbox
						icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
						checkedIcon={<CheckBoxIcon fontSize="small" />}
						style={{ marginRight: 8 }}
						checked={selected}
					/>
					{getOptionLabel(option)}
				</li>
			);
		}}
		renderInput={(params) => (
			<TextField {...params} label={label} helperText={helperText} />
		)}
	/>
);

/**
 * Creates a HubSpot product, or edits one. Saves straight to HubSpot; line
 * items already on deals don't change. Retiring is a separate action.
 * @param {boolean} props.open
 * @param {Object|null} props.product - the product to edit; null to create
 * @param {Object} props.options - { phases, services, components } from HubSpot
 * @param {Function} props.onClose
 * @param {Function} props.onSaved - (product, changed, created) after a save
 */
const ProductDialog = ({ open, product, options, onClose, onSaved }) => {
	// MSAL
	const { instance, accounts } = useMsal();

	// STATE
	const [form, setForm] = useState(formFrom(product));
	const [isSaving, setIsSaving] = useState(false);
	const [errorText, setErrorText] = useState("");

	const isNew = !product;

	useEffect(() => {
		if (open) {
			setForm(formFrom(product));
			setErrorText("");
		}
	}, [open, product]);

	const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

	const handleSave = async () => {
		if (!form.text.trim()) {
			setErrorText("Name can't be empty.");
			return;
		}
		if (form.rate !== "" && !(Number(form.rate) >= 0)) {
			setErrorText("Rate must be a number of 0 or more.");
			return;
		}
		const fields = {
			text: form.text,
			description: form.description,
			rate: form.rate === "" ? null : Number(form.rate),
			phaseNames: form.phaseNames,
			services: form.services,
			components: form.components,
		};
		setIsSaving(true);
		setErrorText("");
		try {
			if (isNew) {
				const res = await postAPICall(
					instance,
					accounts[0],
					"/api/product-library/products",
					{ fields },
				);
				onSaved(res.data.product, [], true);
			} else {
				const res = await patchAPICall(
					instance,
					accounts[0],
					`/api/product-library/products/${product.id}`,
					{ changes: fields, expectedModified: product.modified },
				);
				onSaved(res.data.product, res.data.changed || [], false);
			}
		} catch (err) {
			console.error(err);
			setErrorText(
				err?.response?.data?.error ||
					(isNew
						? "Couldn't create this product."
						: "Couldn't save this product."),
			);
		}
		setIsSaving(false);
	};

	return (
		<Dialog
			open={open}
			fullWidth
			maxWidth="md"
			onClose={isSaving ? undefined : onClose}
		>
			<DialogTitle id="editProductDialogTitle">
				{isNew ? "New Product" : "Edit Product"}
			</DialogTitle>
			<DialogContent id="editProductDialogContent">
				<Collapse in={Boolean(errorText)}>
					<Alert
						severity="error"
						action={
							<IconButton
								aria-label="close"
								color="inherit"
								size="small"
								onClick={() => setErrorText("")}
							>
								<CloseIcon fontSize="inherit" />
							</IconButton>
						}
					>
						{errorText}
					</Alert>
				</Collapse>

				<TextField
					label="Name"
					value={form.text}
					onChange={(e) => set("text", e.target.value)}
					multiline
					minRows={3}
					autoFocus={isNew}
					helperText={
						isNew
							? "The wording added to a phase from the library."
							: "The wording added to a phase from the library. Line items already on deals keep their wording."
					}
				/>

				<Autocomplete
					freeSolo
					options={STAGES}
					value={form.description}
					onChange={(_, v) => set("description", v || "")}
					onInputChange={(_, v) => set("description", v || "")}
					renderInput={(params) => (
						<TextField
							{...params}
							label="Description"
							helperText="HubSpot's product description. On library products it's the stage: Diagnose, Prescribe, Treat or Claim."
						/>
					)}
				/>

				<div className="editProductDialogRow">
					<TextField
						label="Rate"
						value={form.rate}
						onChange={(e) =>
							set("rate", e.target.value.replace(/[^0-9.]/g, ""))
						}
						inputProps={{ inputMode: "decimal" }}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									$
								</InputAdornment>
							),
							endAdornment: (
								<InputAdornment position="end">
									/h
								</InputAdornment>
							),
						}}
						sx={{ flex: 1 }}
					/>
					<MultiSelect
						label="Phase"
						options={(options?.phases || []).map((p) => p.value)}
						value={form.phaseNames}
						onChange={(v) => set("phaseNames", v)}
						getOptionLabel={(v) =>
							options?.phases?.find((p) => p.value === v)
								?.label || v
						}
						sx={{ flex: 2 }}
					/>
				</div>

				<MultiSelect
					label="Service"
					options={options?.services || []}
					value={form.services}
					onChange={(v) => set("services", v)}
					helperText="Suggests this line on deals for these services."
				/>
				<MultiSelect
					label="Component"
					options={options?.components || []}
					value={form.components}
					onChange={(v) => set("components", v)}
					helperText="Suggests this line on deals for these components."
				/>

				<div className="dialogButtons">
					<span className="productLibraryMuted editProductDialogNote">
						{isNew
							? "Creates an active product in HubSpot."
							: "Saves to HubSpot."}
					</span>
					<Button
						onClick={onClose}
						variant="outlined"
						color="error"
						disabled={isSaving}
					>
						Cancel
					</Button>
					<Button
						onClick={handleSave}
						variant="contained"
						disableElevation
						disabled={isSaving}
						startIcon={
							isSaving ? (
								<CircularProgress size={16} color="inherit" />
							) : null
						}
					>
						{isNew ? "Create" : "Save"}
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default ProductDialog;
